/** Matching helpers for the opt-in-to-disable local lyric lookup. */
export type LocalLyricTrack = {
  title?: string;
  artists?: string[];
  album?: string;
  durationMs?: number;
  metadata?: { album?: string; durationMs?: number; [key: string]: unknown };
};
export type LyricCandidate = LocalLyricTrack & {
  id?: string;
  sourceType?: string;
  externalId?: string;
  metadata?: { platform?: string; songId?: string | number; album?: string; durationMs?: number; [key: string]: unknown };
};

const compact = (value: unknown) => String(value || '')
  .normalize('NFKC')
  .toLocaleLowerCase()
  .replace(/[\u0000-\u001f]/g, '')
  .replace(/[\[\]【】()（）{}「」『』<>《》]/g, ' ')
  .replace(/[·•・,，、.!！?？:：;；/\\_|+＆&~～-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const exact = (left: string, right: string) => Boolean(left && right && left === right);

export function localLyricAttemptKey(track: LocalLyricTrack): string {
  return `${String(track.title || '')}\u0000${(track.artists || []).join('\u0000')}`;
}

/**
 * Reserve one lookup per track/query. A failed request must release this
 * marker so a later playback can retry after the network recovers.
 */
export function reserveLocalLyricAttempt(attempts: Map<string, string>, id: string, key: string): boolean {
  if (attempts.get(id) === key) return false;
  attempts.set(id, key);
  return true;
}

export function releaseLocalLyricAttempt(attempts: Map<string, string>, id: string, key: string): void {
  if (attempts.get(id) === key) attempts.delete(id);
}

const durationOf = (track: LocalLyricTrack): number | undefined => {
  const raw = track.durationMs ?? track.metadata?.durationMs;
  const duration = Number(raw);
  return Number.isFinite(duration) && duration > 0 ? duration : undefined;
};

const albumOf = (track: LocalLyricTrack): string => compact(track.album || track.metadata?.album);

// Small mastering/encoding differences are normal; a large gap usually means
// live, remix, or another recording and must not be selected automatically.
const durationCompatible = (local: number | undefined, candidate: number | undefined): boolean => {
  if (local === undefined || candidate === undefined) return true;
  const tolerance = Math.max(3_000, Math.min(10_000, Math.max(local, candidate) * 0.04));
  return Math.abs(local - candidate) <= tolerance;
};

/**
 * Select a likely platform result without registering that result as a local
 * library track. Exact normalized title plus artist is required when the
 * local file has artist metadata; exact title alone is accepted when it does not.
 */
export function selectLocalLyricCandidate(track: LocalLyricTrack, candidates: LyricCandidate[]): LyricCandidate | undefined {
  const title = compact(track.title);
  if (!title) return undefined;
  const artists = (track.artists || []).map(compact).filter(value => value && value !== compact('未知艺术家'));
  const localDuration = durationOf(track);
  const localAlbum = albumOf(track);
  const ranked = candidates.map((candidate, index) => {
    if (candidate.sourceType !== 'online' || !candidate.externalId) return { candidate, index, score: -1 };
    const candidateTitle = compact(candidate.title);
    const titleExact = exact(title, candidateTitle);
    // Automatic lookup must never silently attach a remix/live recording. The
    // normalizer already removes harmless punctuation, so require the title
    // itself to match after normalization.
    if (!titleExact) return { candidate, index, score: -1 };
    const candidateArtists = (candidate.artists || []).map(compact).filter(Boolean);
    const artistMatch = artists.length === 0 || artists.some(local => candidateArtists.some(remote => exact(local, remote)));
    if (!artistMatch) return { candidate, index, score: -1 };
    const candidateDuration = durationOf(candidate);
    if (!durationCompatible(localDuration, candidateDuration)) return { candidate, index, score: -1 };
    const candidateAlbum = albumOf(candidate);
    const durationScore = localDuration !== undefined && candidateDuration !== undefined
      ? Math.max(0, 24 - Math.round(Math.abs(localDuration - candidateDuration) / 1000))
      : 0;
    const albumScore = localAlbum && candidateAlbum ? (localAlbum === candidateAlbum ? 24 : -8) : 0;
    return { candidate, index, score: (artists.length ? 100 : 80) + durationScore + albumScore };
  }).filter(item => item.score >= 0).sort((left, right) => right.score - left.score || left.index - right.index);
  return ranked[0]?.candidate;
}

export function localLyricLookupQuery(track: LocalLyricTrack): string {
  return [track.title, ...(track.artists || []).filter(artist => compact(artist) && compact(artist) !== compact('未知艺术家'))]
    .map(value => String(value || '').trim()).filter(Boolean).join(' ');
}
